import React, {useState, useEffect, useRef} from 'react'
import { Box, Stack, Tabs, Tab, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { useNavigate, useLocation } from "react-router-dom";
import { useReactToPrint } from 'react-to-print';
import { apiURL } from './App';
import { authJson } from './apiClient';
import { SecondButton } from './Controls';
import { SelectDateRange } from './SelectDate';
import EventCalendar from './EventCalendar';
import MarkerViewMap from './MarkerViewMap';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

// 初期表示中心位置
const defaultCenter = {
  lat: parseFloat(process.env.REACT_APP_INIT_LAT || '36.000'),
  lng: parseFloat(process.env.REACT_APP_INIT_LON || '138.000')
};

// ロケールを日本語に設定
dayjs.locale('ja');

// コンテナ
const Container = styled('div')({
//  display: 'flex',
//  flexDirection: 'column',
//  justifyContent: 'flex-start',
//  alignItems: 'flex-start',
  //padding: '30px',
//  backgroundColor: '#fff',
//  position: 'relative',
//  top: '0',
//  bottom: '0',
  width: '100%',
  height: '100%'
});

// タブパネル
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="panel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// スタイル
const styles = {
  grid: {
    // 列ヘッダに背景色を指定
    '.MuiDataGrid-columnHeaders': {
      backgroundColor: '#fff5e5', 
    }
  }
}

// タブ属性設定
function tabProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

// 地図・カレンダー表示ページ
const ViewPage = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [ tabValue, setTabValue ] = useState(props.selectTab);
  const [ rows, setRows ] = useState([]);
  const [ condition, setCondition ] = useState(null);
  const [ period, setPeriod ] = useState([]);
  const [ locations, setLocations ] = useState([]);
  const [ events, setEvents ] = useState([]);
  const contentRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef });

  // 初期ロード
  useEffect(() => {
    // 検索ページから遷移
    if (location.state && location.state.rows) {
      // 取材データリスト設定
      setRows(location.state.rows)
      // 表示期間設定
      setPeriod(location.state.period)
    }
    // 検索ページ以外から遷移
    else {
      // 検索条件初期化
      let form = {}    
      // 検索条件取得
      const json = sessionStorage.getItem("condition")
      if (json) { 
        // Jsonから連想配列へ変換
        form = JSON.parse(json)
      }
      // 検索条件設定
      setCondition(form)

      // 表示期間取得
      const str = sessionStorage.getItem("period")
      if (str) {
        // カンマセパレータから日付型配列へ変換
        const dates = str.split(",").map(date => date ? new Date(date) : null)
        // 表示期間設定
        setPeriod(dates)
      }
    }
  }, [])

  // 検索条件変更
  useEffect(() => {
    // 取材データリスト取得
    fetchRows()
  }, [condition])

  // 表示期間変更または取材データリスト変更
  useEffect(() => {
    // 表示期間保存（カンマセパレータ）
    if (period) sessionStorage.setItem("period", period.toString())
    // 表示期間変更
    updateEvents()
  }, [period, rows])
  
  // タブ変更
  const handleTabChange = (event, newValue) => {
    // 現在のタブと同じ場合
    if (newValue === tabValue) {
      return;
    }
    // リストの場合
    if (newValue == 0) {
      // 検索ページへ
      navigate("/search", {replace: true});
    }
    else if (newValue == 1) {
      // 地図へ
      navigate("/search/view/map", { state: { period: period, rows: rows }, replace: true });
    }
    else if (newValue == 2) {
      // カレンダーへ
      navigate("/search/view/calender", { state: { period: period, rows: rows }, replace: true });
    }
    // タブ切替（地図/カレンダー）
    setTabValue(newValue);
  };

  // 表示期間
  const onChangePeriod = (dates) => {
    // 表示期間設定
    setPeriod(dates);
  }

  // 位置情報リスト・イベントリスト作成
  const updateEvents = () => {
    // 表示期間
    let [start, end] = period;
    if (end == null) {
      end = start
    }

    // 位置情報リスト・イベントリスト初期化
    let newLocations = []
    let newEvents = []

    // 位置情報リスト・イベントリスト作成
    rows.forEach((row, i) => {
      // 取材日範囲判定
      let date = dayjs(row.interview_date);
      if (start == null || (start <= date && end >= date)) {
        // 緯度経度が設定されている場合
        if (row.lon != null && row.lon != 0.0 && row.lat != null && row.lat != 0.0) {
          // 位置情報追加
          newLocations.push({id: row.id, name: row.interview_place, lat: row.lat, lng: row.lon})
        }
        // 開始時刻作成
        let startTime = row.interview_date
        if (row.interview_starttime != null) {
          startTime += " " + row.interview_starttime
        }
        // 終了時刻が設定されている場合
        if (row.interview_endtime != null) {
          // 終了時刻作成
          let endTime = row.interview_date + " " + row.interview_endtime
          // イベント情報追加(開始時刻-終了時刻)
          newEvents.push({id: (i+1), title: row.interview_name, start: startTime, end: endTime})
        }
        else {
          // イベント情報追加(開始時刻)
          newEvents.push({id: (i+1), title: row.interview_name, start: startTime})
        }
      }
    })
    // 位置情報リスト
    setLocations(newLocations)
    // イベントリスト
    setEvents(newEvents)
  }

  // 取材データリスト取得
  const fetchRows = async () => {
    // 検索条件が指定されていない
    if (condition === null) {
      return;
    }
    // キーワードの配列化
    if (condition.keywords && !Array.isArray(condition.keywords)) {
      condition.keywords = condition.keywords.split(/[\s]+/).filter(Boolean);
    }
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(condition))
    console.log(...body.entries());
    // 取材データ検索
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/interviews`, { method: 'POST', body: body, actionName: 'SearchInterviews' });
      // 取材データリスト設定
      setRows(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // 取材データリスト初期化
      setRows([]); 
    }
  }

  // 地図マーカクリック
  const handleMapEditMarkerClick = (marker) => {
    // 詳細入力へ
    navigate(`../../details/${marker.id}`, {state: {referrer: location.pathname}});
  }

  // レンダリング
  return (
    <Container ref={contentRef}>
      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={5} sx={{mx: 5, my: 2, width: '95%'}}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>取材一覧</Typography>
        <Box displayPrint="none" sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: theme.palette.secondary.light }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="リスト" {...tabProps(0)} />
            <Tab label="地図" {...tabProps(1)} />
            <Tab label="カレンダー" {...tabProps(2)} />
          </Tabs>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ mt: 3, width: 300 }}>
          <SelectDateRange id="period" label="表示期間" placeholder="カレンダーから選択" onChange={onChangePeriod} value={period}/>
        </Box>
        <Box displayPrint="none">
          <SecondButton variant="contained" onClick={() => reactToPrintFn()}>印刷</SecondButton>
        </Box>
      </Stack>
      <TabPanel value={tabValue} index={1}>
        <Box display="flex" direction="column" alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
          <Box sx={{position: 'absolute', px: 3, top: '150px', width: '100%', height: '80vh'}}>
            <MarkerViewMap
              markers={locations}
              defaultCenter={defaultCenter}
              onEditMarkerClick={handleMapEditMarkerClick}
            />
          </Box>
        </Box>
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <EventCalendar events={events}/>
      </TabPanel>
    </Container>
  );
};

export default ViewPage;